import { Alert, Box, Button, Divider, Group, Select, Stack, Text, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import dayjs from 'dayjs';

import { adjustToMonday, isMonday } from '../../../lib/date-utils';
import { plural, preventEnterSubmit } from '../../../lib/utils';

export type BulkEditCreativeMediaFormData = {
    week?: string;
    hasTextOverlay?: boolean;
    hasCallToAction?: boolean;
    additionalText?: string;
};

type InternalFormData = {
    week: string;
    hasTextOverlay: string;
    hasCallToAction: string;
    additionalText: string;
};

type BulkEditCreativeMediaModalProps = {
    selectedCount: number;
    onSubmit: (data: BulkEditCreativeMediaFormData) => void;
    onCancel: () => void;
    isSubmitting?: boolean;
};

export const BulkEditCreativeMediaModal = ({
    selectedCount,
    onSubmit,
    onCancel,
    isSubmitting,
}: BulkEditCreativeMediaModalProps) => {
    const form = useForm<InternalFormData>({
        initialValues: {
            week: '',
            hasTextOverlay: '',
            hasCallToAction: '',
            additionalText: '',
        },
        validate: {
            week: (value) => {
                if (value && !isMonday(value)) return 'Week must be a Monday';
                return null;
            },
        },
    });

    const handleSubmit = form.onSubmit((values) => {
        // Strip empty string sentinels to undefined
        const updates: BulkEditCreativeMediaFormData = {};
        if (values.week) updates.week = values.week;
        if (values.hasTextOverlay) updates.hasTextOverlay = values.hasTextOverlay === 'true';
        if (values.hasCallToAction) updates.hasCallToAction = values.hasCallToAction === 'true';
        if (values.additionalText) updates.additionalText = values.additionalText;

        // Skip submit if no changes were made
        if (Object.keys(updates).length === 0) {
            return;
        }

        onSubmit(updates);
    });

    const hasChanges = Object.values(form.values).some((v) => v !== '');

    return (
        <form onSubmit={handleSubmit} onKeyDown={preventEnterSubmit}>
            <Stack gap="lg">
                <Alert color="blue">
                    You are editing <strong>{selectedCount}</strong> creative media{' '}
                    {plural(selectedCount, 'item', 'items')}. Only fields you change will be updated.
                </Alert>

                <Box>
                    <DatePickerInput
                        label="Week"
                        value={form.values.week ? dayjs(form.values.week).toDate() : null}
                        onChange={(date) => {
                            if (date) {
                                form.setFieldValue(
                                    'week',
                                    adjustToMonday(dayjs(date).format('YYYY-MM-DD')).format('YYYY-MM-DD'),
                                );
                            } else {
                                form.setFieldValue('week', '');
                            }
                        }}
                        disabled={isSubmitting}
                        error={form.errors.week}
                        placeholder="No change"
                    />
                    <Text size="xs" c="dimmed" mt={4}>
                        Leave empty to keep existing values. Date will be automatically adjusted to the nearest Monday
                    </Text>
                </Box>

                <Select
                    label="Has Text Overlay"
                    value={form.values.hasTextOverlay}
                    onChange={(val) => form.setFieldValue('hasTextOverlay', val ?? '')}
                    disabled={isSubmitting}
                    data={[
                        { value: '', label: '-- No Change --' },
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' },
                    ]}
                    description='Leave as "No Change" to keep existing values'
                />

                <Select
                    label="Has Call to Action"
                    value={form.values.hasCallToAction}
                    onChange={(val) => form.setFieldValue('hasCallToAction', val ?? '')}
                    disabled={isSubmitting}
                    data={[
                        { value: '', label: '-- No Change --' },
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' },
                    ]}
                    description='Leave as "No Change" to keep existing values'
                />

                <TextInput
                    label="Freeholder (Additional Text)"
                    placeholder="No change"
                    disabled={isSubmitting}
                    description="Leave empty to keep existing values"
                    {...form.getInputProps('additionalText')}
                />

                <Divider />
                <Group justify="flex-end" gap="sm">
                    <Button variant="subtle" onClick={onCancel} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isSubmitting || !hasChanges}>
                        {isSubmitting
                            ? 'Updating...'
                            : `Update ${selectedCount} ${plural(selectedCount, 'item', 'items')}`}
                    </Button>
                </Group>
            </Stack>
        </form>
    );
};
